import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MdPlayArrow, MdTune, MdExpandMore, MdExpandLess, MdLayers, MdStop, MdGridView } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import PlotlyChart from '../../components/charts/PlotlyChart';
import MetricCard from '../../components/shared/MetricCard';

// Price-provenance switch. Two rails, applied uniformly to every symbol/run:
//   'consolidated' — merged best-of-vendors adjusted series (legacy default).
//   'raw'          — our raw ledger with split/dividend total-return computed on
//                    the fly (the new pipeline).
// A single switch drives BOTH the P&L frame AND the relative-momentum factor
// feed, so raw ⇒ everything raw, consolidated ⇒ everything consolidated. Legacy
// single-vendor values (fmp/yfinance/ibkr) have no factor-feed equivalent and are
// intentionally not offered here — they remain callable via the API for
// single-symbol diagnostics only.
const PRICE_SOURCE_OPTIONS = [
  { value: 'consolidated', label: 'Consolidated' },
  { value: 'raw', label: 'Raw' },
];

const BacktestPage = () => {
  const [searchParams] = useSearchParams();

  // Available strategies from API
  const [availableStrategies, setAvailableStrategies] = useState([]);
  const [userStrategies, setUserStrategies] = useState([]);

  // Form state
  const [strategyName, setStrategyName] = useState(searchParams.get('strategy') || 'ema_crossover');
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || '');
  const [startDate, setStartDate] = useState('2023-01-01');
  const [endDate, setEndDate] = useState('');
  const [source, setSource] = useState('consolidated');
  const [parameters, setParameters] = useState({});
  const [strategyId, setStrategyId] = useState(searchParams.get('strategy_id') || '');

  // Mode: single or optimize
  const [mode, setMode] = useState('single');
  const [paramRanges, setParamRanges] = useState({});
  const [maximize, setMaximize] = useState('Sharpe Ratio');

  // Optimize target: 'single' symbol vs 'universe' sweep (batch). Only relevant
  // when mode === 'optimize'.
  const [target, setTarget] = useState('single');
  const [uniShortcuts, setUniShortcuts] = useState([]);   // e.g. ['sp500']
  const [uniText, setUniText] = useState('');             // pasted ticker list
  const [inflightCap, setInflightCap] = useState(2);
  const [batchPreview, setBatchPreview] = useState(null); // dry_run result
  const [previewLoading, setPreviewLoading] = useState(false);
  const [batch, setBatch] = useState(null);               // launched batch + polled progress
  const [batchLaunching, setBatchLaunching] = useState(false);
  // Universe refinement (on-demand FMP market cap + sector). Empty string = off.
  const [mcapMinBn, setMcapMinBn] = useState('');         // $bn threshold, e.g. 100
  const [topNPerSector, setTopNPerSector] = useState(''); // top N by mcap per sector
  // Post-hoc member filter on a launched batch (display-only).
  const [memberSectorFilter, setMemberSectorFilter] = useState(''); // '' = all
  const [memberFundsLoaded, setMemberFundsLoaded] = useState(false);

  // Universe shortcut chips — keys must match backend FILTER_SHORTCUTS.
  const UNIVERSE_SHORTCUTS = [
    { key: 'sp500', label: 'S&P 500' },
    { key: 'nasdaq100', label: 'Nasdaq 100' },
    { key: 'dow30', label: 'Dow 30' },
    { key: 'tech', label: 'Technology' },
    { key: 'finance', label: 'Financials' },
    { key: 'healthcare', label: 'Healthcare' },
    { key: 'energy', label: 'Energy' },
    { key: 'industrials', label: 'Industrials' },
    { key: 'consumer', label: 'Consumer Cyc.' },
    { key: 'etfs', label: 'ETFs' },
    // 'us50' is not a stored index — backend realises it as top-50-by-live-mcap
    // over the S&P 500. Kept last + visually distinct as a derived shortcut.
    { key: 'us50', label: 'US 50', derived: true },
  ];
  const isUniverse = mode === 'optimize' && target === 'universe';
  const pastedSymbols = uniText
    .split(/[\s,;\n]+/).map(s => s.trim().toUpperCase()).filter(Boolean);

  // Preflight data check
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  // Results
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  // Sections
  const [configOpen, setConfigOpen] = useState(true);
  const [metricsOpen, setMetricsOpen] = useState(true);
  const [tradesOpen, setTradesOpen] = useState(false);

  // Load available strategies
  useEffect(() => {
    tradingService.getAvailableStrategies()
      .then(res => {
        setAvailableStrategies(res.data || []);
      })
      .catch(() => {});
    tradingService.getStrategies()
      .then(res => setUserStrategies(res.data?.results || res.data || []))
      .catch(() => {});
  }, []);

  // When strategy changes, update default parameters
  const selectedStrategy = useMemo(() => {
    return availableStrategies.find(s => s.name === strategyName);
  }, [availableStrategies, strategyName]);

  useEffect(() => {
    if (selectedStrategy?.parameters) {
      setParameters(prev => {
        // Only set if params are empty or strategy changed
        const hasValues = Object.keys(prev).length > 0;
        if (!hasValues) return { ...selectedStrategy.parameters };
        return prev;
      });
    }
    if (selectedStrategy?.parameter_ranges) {
      setParamRanges(selectedStrategy.parameter_ranges);
    }
  }, [selectedStrategy]);

  // Initialize params on first load if strategy came from URL
  useEffect(() => {
    if (selectedStrategy?.parameters && Object.keys(parameters).length === 0) {
      setParameters({ ...selectedStrategy.parameters });
    }
  }, [selectedStrategy, parameters]);

  // Auto-trigger preflight when symbol or dates change
  useEffect(() => {
    if (!symbol.trim()) { setPreflight(null); return; }
    const timer = setTimeout(async () => {
      setPreflightLoading(true);
      try {
        const res = await tradingService.backtestPreflight({
          symbol: symbol.trim().toUpperCase(),
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          source_priority: ['fmp', 'yfinance', 'ibkr'],
        });
        setPreflight(res.data);
      } catch {
        setPreflight(null);
      } finally {
        setPreflightLoading(false);
      }
    }, 500); // Debounce
    return () => clearTimeout(timer);
  }, [symbol, startDate, endDate]);

  const handleRun = useCallback(async () => {
    if (!symbol.trim()) {
      setError('Please enter a symbol');
      return;
    }

    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        strategy: strategyName,
        symbol: symbol.trim().toUpperCase(),
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        source,
        mode,
      };

      if (mode === 'single') {
        payload.parameters = parameters;
      } else {
        payload.param_ranges = paramRanges;
        payload.maximize = maximize;
      }

      if (strategyId) {
        payload.strategy_id = parseInt(strategyId);
      }

      const res = await tradingService.runBacktest(payload);

      // Async optimization — poll for results
      if (res.data?.async && res.data?.optimization_id) {
        const optId = res.data.optimization_id;
        setError(null);
        setResult({ async: true, optimization_id: optId, total_combinations: res.data.total_combinations, status: 'running', progress: '0/?' });

        const poll = setInterval(async () => {
          try {
            const statusRes = await tradingService.getOptimizationStatus(optId);
            const d = statusRes.data;
            if (d.status === 'complete') {
              clearInterval(poll);
              setResult({ success: true, mode: 'optimize', ...d });
              setRunning(false);
              setConfigOpen(false);
              setMetricsOpen(true);
            } else if (d.status === 'failed') {
              clearInterval(poll);
              setError(d.error_message || 'Optimization failed');
              setRunning(false);
            } else {
              setResult(prev => ({ ...prev, progress: d.progress, completed_combos: d.completed_combos, total_combos: d.total_combos }));
            }
          } catch { /* ignore */ }
        }, 3000);
        return; // Don't setRunning(false) — keep spinner until poll completes
      }

      if (res.data?.success) {
        setResult(res.data);
        setConfigOpen(false);
        setMetricsOpen(true);
      } else {
        setError(res.data?.error || 'Backtest failed');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Backtest request failed');
    } finally {
      setRunning(false);
    }
  }, [strategyName, symbol, startDate, endDate, source, mode, parameters, paramRanges, maximize, strategyId]);

  // ── Universe batch: build the {universe} selector for the API ──────────────
  // Refinement filters (on-demand FMP market cap + sector). Sent only when set.
  const refineFilters = useMemo(() => {
    const r = {};
    const mc = parseFloat(mcapMinBn);
    if (!Number.isNaN(mc) && mc > 0) r.market_cap_min = Math.round(mc * 1e9);
    const tn = parseInt(topNPerSector, 10);
    if (!Number.isNaN(tn) && tn > 0) r.top_n_per_sector = tn;
    return r;
  }, [mcapMinBn, topNPerSector]);

  const buildUniverse = useCallback(() => ({
    shortcuts: uniShortcuts,
    symbols: pastedSymbols,
    ...refineFilters,
  }), [uniShortcuts, pastedSymbols, refineFilters]);

  // Debounced dry-run preview: resolves the universe (count + total combos +
  // unknown tickers) WITHOUT creating the batch, so the user sees the scale
  // before committing.
  useEffect(() => {
    if (!isUniverse) { setBatchPreview(null); return; }
    if (uniShortcuts.length === 0 && pastedSymbols.length === 0) {
      setBatchPreview(null); return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await tradingService.runOptimizeBatch({
          dry_run: true,
          strategy: strategyName,
          universe: { shortcuts: uniShortcuts, symbols: pastedSymbols, ...refineFilters },
          param_ranges: paramRanges,
        });
        if (!cancelled) setBatchPreview(res.data);
      } catch (e) {
        if (!cancelled) setBatchPreview({ error: e.response?.data?.error || 'Preview failed' });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUniverse, uniShortcuts, uniText, strategyName, paramRanges, refineFilters]);

  // Poll batch progress while a batch is active. Once the user has loaded
  // sector/mcap (memberFundsLoaded), keep polling WITH fundamentals so the
  // member filter survives refreshes.
  useEffect(() => {
    if (!batch?.id) return;
    if (['complete', 'cancelled'].includes(batch.status)) return;
    const poll = setInterval(async () => {
      try {
        const res = await tradingService.getOptimizeBatchStatus(batch.id, memberFundsLoaded);
        setBatch(res.data);
      } catch { /* ignore transient */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [batch?.id, batch?.status, memberFundsLoaded]);

  // Fetch per-member sector + market cap on demand (so the regular poll stays
  // light). Enables the sector filter over a launched batch's members.
  const loadMemberFundamentals = useCallback(async () => {
    if (!batch?.id) return;
    try {
      const res = await tradingService.getOptimizeBatchStatus(batch.id, true);
      setBatch(res.data);
      setMemberFundsLoaded(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load sectors');
    }
  }, [batch?.id]);

  const handleLaunchBatch = useCallback(async () => {
    setBatchLaunching(true);
    setError(null);
    try {
      const res = await tradingService.runOptimizeBatch({
        strategy: strategyName,
        universe: buildUniverse(),
        param_ranges: paramRanges,
        maximize,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        source,
        inflight_cap: inflightCap,
      });
      if (res.data?.success) {
        // Seed a minimal batch object; the poll effect fills in full progress.
        setMemberFundsLoaded(false);
        setMemberSectorFilter('');
        setBatch({ id: res.data.batch_id, status: 'queued', counts: {}, members: [],
                   total_symbols: res.data.total_symbols, pct_complete: 0,
                   name: `${strategyName} sweep` });
        setConfigOpen(false);
      } else {
        setError(res.data?.error || 'Failed to launch batch');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Batch launch failed');
    } finally {
      setBatchLaunching(false);
    }
  }, [strategyName, buildUniverse, paramRanges, maximize, startDate, endDate, source, inflightCap]);

  const handleCancelBatch = useCallback(async () => {
    if (!batch?.id) return;
    try {
      await tradingService.cancelOptimizeBatch(batch.id);
      setBatch(prev => prev ? { ...prev, cancel_requested: true } : prev);
    } catch (e) {
      setError(e.response?.data?.error || 'Cancel failed');
    }
  }, [batch?.id]);

  // Distinct sectors present in the launched batch (once fundamentals loaded).
  const batchSectors = useMemo(() => {
    const s = new Set((batch?.members || []).map(m => m.sector).filter(Boolean));
    return Array.from(s).sort();
  }, [batch?.members]);

  // Members after applying the post-hoc sector filter (display-only).
  const visibleMembers = useMemo(() => {
    const members = batch?.members || [];
    if (!memberSectorFilter) return members;
    return members.filter(m => m.sector === memberSectorFilter);
  }, [batch?.members, memberSectorFilter]);

  const cardCls = 'bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4';
  const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold';
  const labelCls = 'text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block';

  // Build price chart with buy/sell markers
  const priceChart = useMemo(() => {
    const prices = result?.price_series;
    if (!prices?.length) return null;
    const trades = result?.trades || [];
    const traces = [
      {
        x: prices.map(p => p.date),
        y: prices.map(p => p.close),
        type: 'scatter', mode: 'lines', name: 'Price',
        line: { color: '#6b7280', width: 1.5 },
      },
    ];
    if (trades.length > 0) {
      traces.push({
        x: trades.map(t => t.entry_date).filter(Boolean),
        y: trades.map(t => t.entry_price).filter(Boolean),
        type: 'scatter', mode: 'markers', name: 'Buy',
        marker: { symbol: 'triangle-up', size: 10, color: '#22c55e', line: { width: 1, color: '#166534' } },
        hovertemplate: 'BUY %{x|%b %d %Y}<br>$%{y:,.2f}<extra></extra>',
      });
      traces.push({
        x: trades.map(t => t.exit_date).filter(Boolean),
        y: trades.map(t => t.exit_price).filter(v => v),
        type: 'scatter', mode: 'markers', name: 'Sell',
        marker: { symbol: 'triangle-down', size: 10, color: '#ef4444', line: { width: 1, color: '#991b1b' } },
        hovertemplate: 'SELL %{x|%b %d %Y}<br>$%{y:,.2f}<extra></extra>',
      });
    }
    return traces;
  }, [result]);

  // Build equity curve chart
  const equityCurveChart = useMemo(() => {
    if (!result?.equity_curve?.length) return null;
    return [{
      x: result.equity_curve.map(e => e.date),
      y: result.equity_curve.map(e => e.equity),
      type: 'scatter', mode: 'lines', name: 'Equity',
      line: { color: '#c9a96e', width: 2 },
      fill: 'tozeroy', fillcolor: 'rgba(201, 169, 110, 0.1)',
    }];
  }, [result]);

  // Build drawdown chart
  const drawdownChart = useMemo(() => {
    if (!result?.equity_curve?.length) return null;
    const dd = result.equity_curve.filter(e => e.drawdown != null);
    if (dd.length === 0) return null;
    return [{
      x: dd.map(e => e.date),
      y: dd.map(e => -Math.abs(e.drawdown) * 100),
      type: 'scatter',
      mode: 'lines',
      name: 'Drawdown',
      line: { color: '#ef4444', width: 1 },
      fill: 'tozeroy',
      fillcolor: 'rgba(239, 68, 68, 0.15)',
    }];
  }, [result]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-serif font-semibold text-gray-800 dark:text-gray-200">BACKTEST ENGINE</h2>
        {result && (
          <button
            onClick={() => { setResult(null); setConfigOpen(true); }}
            className="text-xs text-brand-gold hover:underline"
          >
            New Backtest
          </button>
        )}
      </div>

      {/* Configuration */}
      <div className={cardCls}>
        <button
          onClick={() => setConfigOpen(!configOpen)}
          className="flex items-center justify-between w-full"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">CONFIGURATION</h3>
          {configOpen ? <MdExpandLess className="text-gray-400" /> : <MdExpandMore className="text-gray-400" />}
        </button>

        {configOpen && (
          <div className="mt-4">
            {/* Mode toggle */}
            <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 w-fit">
              <button
                onClick={() => setMode('single')}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  mode === 'single'
                    ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Single Run
              </button>
              <button
                onClick={() => setMode('optimize')}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  mode === 'optimize'
                    ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <MdTune size={12} /> Optimize
              </button>
            </div>

            {/* Target sub-toggle (optimize only): single symbol vs universe sweep */}
            {mode === 'optimize' && (
              <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 w-fit">
                <button
                  onClick={() => setTarget('single')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                    target === 'single'
                      ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Single Symbol
                </button>
                <button
                  onClick={() => setTarget('universe')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                    target === 'universe'
                      ? 'bg-white dark:bg-[#0d1117] text-brand-gold shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <MdLayers size={12} /> Universe
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {/* Strategy selector */}
              <div>
                <label className={labelCls}>Strategy</label>
                <select
                  className={inputCls}
                  value={strategyName}
                  onChange={e => {
                    setStrategyName(e.target.value);
                    setParameters({});
                  }}
                >
                  {availableStrategies.map(s => (
                    <option key={s.name} value={s.name}>{s.display_name || s.name}</option>
                  ))}
                  {availableStrategies.length === 0 && (
                    <option value="ema_crossover">EMA Crossover</option>
                  )}
                </select>
              </div>

              {/* Symbol — hidden in universe mode (symbol set is chosen below) */}
              {!isUniverse && (
                <div>
                  <label className={labelCls}>Symbol</label>
                  <input
                    className={inputCls}
                    value={symbol}
                    onChange={e => setSymbol(e.target.value.toUpperCase())}
                    placeholder="AAPL"
                  />
                </div>
              )}

              {/* Start date */}
              <div>
                <label className={labelCls}>Start Date</label>
                <input
                  type="date"
                  className={inputCls}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>

              {/* End date */}
              <div>
                <label className={labelCls}>End Date</label>
                <input
                  type="date"
                  className={inputCls}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  placeholder="Today"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {/* Source */}
              <div>
                <label className={labelCls}>Data Source</label>
                <select className={inputCls} value={source} onChange={e => setSource(e.target.value)}>
                  {PRICE_SOURCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Link to user strategy (optional) */}
              <div>
                <label className={labelCls}>Save to Strategy (optional)</label>
                <select className={inputCls} value={strategyId} onChange={e => setStrategyId(e.target.value)}>
                  <option value="">-- None --</option>
                  {userStrategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {mode === 'optimize' && (
                <div>
                  <label className={labelCls}>Maximize</label>
                  <select className={inputCls} value={maximize} onChange={e => setMaximize(e.target.value)}>
                    <option value="Sharpe Ratio">Sharpe Ratio</option>
                    <option value="Return">Total Return</option>
                    <option value="Win Rate">Win Rate</option>
                  </select>
                </div>
              )}
            </div>

            {/* Universe selector (optimize + universe target) */}
            {isUniverse && (
              <div className="mb-4 rounded-lg border border-brand-gold/30 bg-brand-gold/5 p-3">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                  <MdLayers size={12} /> Universe — runs this exact config across every resolved symbol
                </h4>

                {/* Shortcut chips (multi-select, unioned) */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {UNIVERSE_SHORTCUTS.map(sc => {
                    const on = uniShortcuts.includes(sc.key);
                    return (
                      <button
                        key={sc.key}
                        onClick={() => setUniShortcuts(prev =>
                          on ? prev.filter(k => k !== sc.key) : [...prev, sc.key])}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-full border transition-colors ${
                          on
                            ? 'bg-brand-gold text-white border-brand-gold'
                            : 'bg-white dark:bg-[#0d1117] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:border-brand-gold'
                        }`}
                      >
                        {sc.label}
                      </button>
                    );
                  })}
                </div>

                {/* Paste-a-list textarea */}
                <label className="text-[10px] text-gray-400 mb-1 block">
                  Or paste tickers (any of comma / space / newline separated) — unioned with chips
                </label>
                <textarea
                  className={`${inputCls} h-16 font-mono text-xs`}
                  value={uniText}
                  onChange={e => setUniText(e.target.value)}
                  placeholder="AAPL, MSFT, NVDA&#10;TSLA GOOGL"
                />

                {/* Refinement filters — on-demand live market cap (FMP). Empty = off. */}
                <div className="mt-3 pt-3 border-t border-brand-gold/20">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5 block">
                    Refine by market cap & sector — current cap fetched live, not stored
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-gray-400 mb-1 block">Min market cap ($bn)</label>
                      <input
                        type="number" min={0} step={10} placeholder="e.g. 100"
                        className={`${inputCls} text-xs`}
                        value={mcapMinBn}
                        onChange={e => setMcapMinBn(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 mb-1 block">Top N by cap / sector</label>
                      <input
                        type="number" min={0} step={1} placeholder="e.g. 5"
                        className={`${inputCls} text-xs`}
                        value={topNPerSector}
                        onChange={e => setTopNPerSector(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Symbols with no market-cap data are dropped from cap/sector filters (never silently ranked).
                  </p>
                </div>

                {/* Inflight cap */}
                <div className="mt-3 flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">Concurrent runs</label>
                  <input
                    type="number" min={1} max={8}
                    className="w-16 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-xs"
                    value={inflightCap}
                    onChange={e => setInflightCap(Math.max(1, Math.min(8, parseInt(e.target.value) || 2)))}
                  />
                  <span className="text-[10px] text-gray-400">
                    members dispatched to the spot worker at once (each fans 8 IS slices — 2 is the sweet spot)
                  </span>
                </div>

                {/* Resolved-count preview / scale confirmation */}
                {previewLoading && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-brand-gold" />
                    Resolving universe…
                  </div>
                )}
                {batchPreview && !previewLoading && !batchPreview.error && (
                  <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500 block">Symbols resolved</span>
                        <span className="text-base font-semibold text-brand-gold">{batchPreview.total_symbols?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Combos / symbol</span>
                        <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{batchPreview.combos_per_member?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Total combos</span>
                        <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{batchPreview.estimated_total_combos?.toLocaleString()}</span>
                      </div>
                    </div>
                    {batchPreview.refined && batchPreview.sector_breakdown
                      && Object.keys(batchPreview.sector_breakdown).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">By sector</span>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(batchPreview.sector_breakdown)
                            .sort((a, b) => b[1] - a[1])
                            .map(([sec, n]) => (
                              <span key={sec} className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                {sec} · {n}
                              </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {batchPreview.dropped_no_mcap?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-500">
                        {batchPreview.dropped_no_mcap.length} symbol(s) dropped — no market-cap data from FMP.
                      </div>
                    )}
                    {batchPreview.unknown_symbols?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-orange-600 dark:text-orange-400">
                        {batchPreview.unknown_symbols.length} unknown ticker(s) skipped: {batchPreview.unknown_symbols.slice(0, 12).join(', ')}{batchPreview.unknown_symbols.length > 12 ? '…' : ''}
                      </div>
                    )}
                  </div>
                )}
                {batchPreview?.error && !previewLoading && (
                  <div className="mt-3 text-xs text-red-600 dark:text-red-400">{batchPreview.error}</div>
                )}
              </div>
            )}

            {/* Parameters */}
            {mode === 'single' ? (
              <div className="mb-4">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">PARAMETERS</h4>
                {selectedStrategy?.parameters ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(selectedStrategy.parameters).map(([key, defaultVal]) => (
                      <div key={key}>
                        <label className="text-[10px] text-gray-400 mb-1 block font-mono">{key}</label>
                        <input
                          type="number"
                          className={inputCls}
                          value={parameters[key] ?? defaultVal}
                          onChange={e => setParameters(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] text-gray-400 mb-1 block">Parameters JSON</label>
                    <input
                      className={inputCls}
                      value={JSON.stringify(parameters)}
                      onChange={e => { try { setParameters(JSON.parse(e.target.value)); } catch (_) {} }}
                      placeholder='{"fast_ema": 9, "slow_ema": 21}'
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">PARAMETER RANGES (for optimization)</h4>
                {selectedStrategy?.parameter_ranges ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(selectedStrategy.parameter_ranges).map(([key, values]) => (
                      <div key={key}>
                        <label className="text-[10px] text-gray-400 mb-1 block font-mono">{key}</label>
                        <input
                          className={inputCls}
                          value={JSON.stringify(paramRanges[key] || values)}
                          onChange={e => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setParamRanges(prev => ({ ...prev, [key]: parsed }));
                            } catch (_) {}
                          }}
                          placeholder={JSON.stringify(values)}
                        />
                        <span className="text-[9px] text-gray-400 mt-0.5 block">Array of values, e.g. [5, 10, 15, 20]</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] text-gray-400 mb-1 block">Parameter Ranges JSON</label>
                    <textarea
                      className={`${inputCls} h-20 font-mono text-xs`}
                      value={JSON.stringify(paramRanges, null, 2)}
                      onChange={e => { try { setParamRanges(JSON.parse(e.target.value)); } catch (_) {} }}
                      placeholder='{"fast_ema": [5, 9, 12], "slow_ema": [20, 30, 50]}'
                    />
                  </div>
                )}
              </div>
            )}

            {/* Preflight data availability summary */}
            {preflightLoading && (
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-brand-gold" />
                Checking data availability...
              </div>
            )}
            {preflight && !preflightLoading && (
              <div className={`rounded-lg border p-3 mb-3 text-sm ${
                preflight.recommendation === 'good' ? 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20' :
                preflight.recommendation === 'acceptable' ? 'border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20' :
                preflight.recommendation === 'warning' ? 'border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20' :
                'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    Data: {preflight.coverage.fill_rate}% coverage
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    preflight.recommendation === 'good' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                    preflight.recommendation === 'acceptable' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400' :
                    preflight.recommendation === 'warning' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400' :
                    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                  }`}>
                    {preflight.recommendation}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{preflight.message}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Range</span>
                    <div className="font-medium text-gray-800 dark:text-gray-200">{preflight.requested_range.start} — {preflight.requested_range.end}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Trading Days</span>
                    <div className="font-medium text-gray-800 dark:text-gray-200">{preflight.coverage.consolidated_covered} / {preflight.coverage.total_trading_days}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Gaps</span>
                    <div className="font-medium text-gray-800 dark:text-gray-200">{preflight.coverage.gaps_count}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Sources Used</span>
                    <div className="flex gap-1 mt-0.5">
                      {Object.entries(preflight.coverage.consolidated_by_source || {}).filter(([,v]) => v > 0).map(([src, cnt]) => (
                        <span key={src} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          src === 'fmp' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                          src === 'yfinance' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400' :
                          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                        }`}>{src}: {cnt}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {preflight.per_source && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Per-Source Fill Rate</div>
                    <div className="flex gap-3">
                      {preflight.source_priority.map(src => {
                        const info = preflight.per_source[src];
                        if (!info) return null;
                        return (
                          <span key={src} className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">{src}</span>: {info.fill_rate}% ({info.days_in_range}d)
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Run button */}
            {isUniverse ? (
              <button
                onClick={handleLaunchBatch}
                disabled={batchLaunching || !(batchPreview?.total_symbols > 0)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-brand-gold text-white text-sm font-medium disabled:opacity-40 hover:bg-brand-gold-dark transition-colors"
              >
                {batchLaunching ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Launching sweep…
                  </>
                ) : (
                  <>
                    <MdLayers size={16} />
                    {batchPreview?.total_symbols > 0
                      ? `Launch Sweep — ${batchPreview.total_symbols.toLocaleString()} symbols`
                      : 'Launch Universe Sweep'}
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleRun}
                disabled={running || !symbol.trim()}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-brand-gold text-white text-sm font-medium disabled:opacity-40 hover:bg-brand-gold-dark transition-colors"
              >
                {running ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    {mode === 'optimize' ? 'Optimizing...' : 'Running Backtest...'}
                  </>
                ) : (
                  <>
                    <MdPlayArrow size={16} />
                    {mode === 'optimize' ? 'Run Optimization' : 'Run Backtest'}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Batch progress card */}
      {batch && (
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MdGridView className="text-brand-gold" />
              <h3 className="text-sm font-serif font-semibold text-gray-800 dark:text-gray-200">
                Universe Sweep #{batch.id}
              </h3>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                batch.status === 'complete' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                batch.status === 'cancelled' ? 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
                'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
              }`}>
                {batch.cancel_requested && batch.status !== 'cancelled' ? 'cancelling' : batch.status}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {!['complete', 'cancelled'].includes(batch.status) && !batch.cancel_requested && (
                <button
                  onClick={handleCancelBatch}
                  className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline"
                >
                  <MdStop size={14} /> Cancel
                </button>
              )}
              <button onClick={() => setBatch(null)} className="text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex justify-between text-[10px] text-gray-500 mb-1">
              <span>{batch.counts?.terminal || 0} / {batch.total_symbols} done</span>
              <span>{batch.pct_complete ?? 0}%</span>
            </div>
            <div className="h-2 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-brand-gold transition-all" style={{ width: `${batch.pct_complete ?? 0}%` }} />
            </div>
          </div>

          {/* Count chips */}
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
            {[
              ['queued', batch.counts?.queued, 'text-gray-500'],
              ['running', batch.counts?.running, 'text-blue-600 dark:text-blue-400'],
              ['complete', batch.counts?.complete, 'text-green-600 dark:text-green-400'],
              ['failed', batch.counts?.failed, 'text-red-600 dark:text-red-400'],
            ].map(([label, n, cls]) => (
              <span key={label} className={`px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 ${cls}`}>
                {label}: {n || 0}
              </span>
            ))}
          </div>

          {/* Post-hoc member filter — load live sector/mcap, then filter the grid */}
          {batch.members?.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              {!memberFundsLoaded ? (
                <button
                  onClick={loadMemberFundamentals}
                  className="text-[11px] text-brand-gold hover:underline"
                >
                  Load sectors to filter
                </button>
              ) : (
                <>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500">Sector</label>
                  <select
                    value={memberSectorFilter}
                    onChange={e => setMemberSectorFilter(e.target.value)}
                    className="bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-xs text-gray-700 dark:text-gray-300"
                  >
                    <option value="">All sectors ({batch.members.length})</option>
                    {batchSectors.map(sec => (
                      <option key={sec} value={sec}>
                        {sec} ({batch.members.filter(m => m.sector === sec).length})
                      </option>
                    ))}
                  </select>
                  {memberSectorFilter && (
                    <button
                      onClick={() => setMemberSectorFilter('')}
                      className="text-[11px] text-gray-400 hover:text-gray-600"
                    >
                      clear
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Member grid */}
          {batch.members?.length > 0 && (
            <div className="max-h-64 overflow-y-auto scrollbar-thin">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                {visibleMembers.map(m => (
                  <div
                    key={m.id}
                    title={m.error_message || `${m.symbol}${m.sector ? ' · ' + m.sector : ''}${m.market_cap ? ' · $' + Math.round(m.market_cap / 1e9) + 'bn' : ''} · ${m.status} · oos=${m.oos_status || '–'} · nsr=${m.nsr_status || '–'}`}
                    className={`flex items-center justify-between rounded px-2 py-1 text-[11px] border ${
                      m.status === 'failed' ? 'border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-900/10' :
                      m.terminal ? 'border-green-300 dark:border-green-900 bg-green-50 dark:bg-green-900/10' :
                      m.dispatch_state === 'released' ? 'border-blue-300 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/10' :
                      'border-gray-200 dark:border-gray-800'
                    }`}
                  >
                    <span className="font-mono font-medium text-gray-700 dark:text-gray-300 truncate">{m.symbol}</span>
                    <span className="text-gray-400 ml-1 shrink-0">
                      {m.status === 'failed' ? '✕' :
                       m.terminal ? '✓' :
                       m.dispatch_state === 'released' ? '⟳' : '·'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">&times;</button>
        </div>
      )}

      {/* Loading */}
      {running && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {mode === 'optimize' ? 'Optimizing parameters... this may take a minute.' : 'Running backtest...'}
          </p>
        </div>
      )}

      {/* Results: Single mode */}
      {result && mode === 'single' && result.metrics && (
        <>
          {/* Metrics */}
          <div className={cardCls}>
            <button
              onClick={() => setMetricsOpen(!metricsOpen)}
              className="flex items-center justify-between w-full mb-2"
            >
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                BACKTEST RESULTS &mdash; {result.symbol} ({result.strategy})
              </h3>
              {metricsOpen ? <MdExpandLess className="text-gray-400" /> : <MdExpandMore className="text-gray-400" />}
            </button>

            {metricsOpen && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
                <MetricCard
                  label="TOTAL RETURN"
                  value={`${result.metrics.total_return >= 0 ? '+' : ''}${result.metrics.total_return.toFixed(1)}%`}
                  valueColor={result.metrics.total_return >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                />
                <MetricCard
                  label="SHARPE RATIO"
                  value={result.metrics.sharpe_ratio.toFixed(2)}
                  valueColor={result.metrics.sharpe_ratio >= 1 ? 'text-green-600 dark:text-green-400' : undefined}
                />
                <MetricCard
                  label="MAX DRAWDOWN"
                  value={`${result.metrics.max_drawdown.toFixed(1)}%`}
                  valueColor="text-red-500 dark:text-red-400"
                />
                <MetricCard
                  label="WIN RATE"
                  value={`${result.metrics.win_rate.toFixed(1)}%`}
                  valueColor={result.metrics.win_rate >= 50 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                />
                <MetricCard
                  label="PROFIT FACTOR"
                  value={result.metrics.profit_factor.toFixed(2)}
                  valueColor={result.metrics.profit_factor >= 1 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                />
                <MetricCard
                  label="# TRADES"
                  value={result.metrics.num_trades}
                />
              </div>
            )}

            {metricsOpen && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <MetricCard
                  label="ANN. RETURN"
                  value={`${result.metrics.annual_return?.toFixed(1) || '--'}%`}
                  valueColor={result.metrics.annual_return >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                />
                <MetricCard
                  label="ANN. VOLATILITY"
                  value={`${result.metrics.annual_volatility?.toFixed(1) || '--'}%`}
                />
                <MetricCard
                  label="BUY & HOLD"
                  value={`${result.metrics.buy_hold_return?.toFixed(1) || '--'}%`}
                  valueColor={result.metrics.buy_hold_return >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                />
                <MetricCard
                  label="EXPOSURE"
                  value={`${result.metrics.exposure_pct?.toFixed(1) || '--'}%`}
                />
              </div>
            )}
          </div>

          {/* Price chart with buy/sell markers + indicator subplot */}
          {priceChart && (
            <div className={cardCls}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">PRICE & TRADES</h3>
              <PlotlyChart
                data={priceChart}
                layout={{
                  yaxis: { title: `${result?.symbol || ''} Price ($)`, tickprefix: '$' },
                  showlegend: true,
                  legend: { x: 0, y: 1, font: { size: 10 } },
                }}
              />
            </div>
          )}

          {/* Strategy Indicators subplot */}
          {result?.indicators && Object.keys(result.indicators).length > 0 && (() => {
            const indicators = result.indicators;
            const names = Object.keys(indicators);
            // Group: overlay indicators (contain 'ema','sma','ma','band','upper','lower') go on price axis
            // Separate indicators (rsi, macd, signal, histogram, momentum, zscore, beta) get own subplot
            const overlayKeys = names.filter(n => /ema|sma|ma_|band|upper|lower|bollinger/i.test(n));
            const subplotKeys = names.filter(n => !overlayKeys.includes(n));
            const colors = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
            return (
              <>
                {overlayKeys.length > 0 && (
                  <div className={cardCls}>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">PRICE + OVERLAYS</h3>
                    <PlotlyChart
                      data={[
                        ...(result.price_series ? [{
                          x: result.price_series.map(p => p.date), y: result.price_series.map(p => p.close),
                          type: 'scatter', mode: 'lines', name: 'Price',
                          line: { color: '#6b7280', width: 1.5 },
                        }] : []),
                        ...overlayKeys.map((key, i) => ({
                          x: indicators[key].map(p => p.date), y: indicators[key].map(p => p.value),
                          type: 'scatter', mode: 'lines', name: key.replace(/_/g, ' '),
                          line: { color: colors[i % colors.length], width: 1, dash: i > 1 ? 'dash' : 'solid' },
                        })),
                      ]}
                      layout={{
                        yaxis: { title: 'Price ($)', tickprefix: '$' },
                        showlegend: true, legend: { x: 0, y: 1, font: { size: 10 } },
                      }}
                    />
                  </div>
                )}
                {subplotKeys.length > 0 && (
                  <div className={cardCls}>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">STRATEGY INDICATORS</h3>
                    <PlotlyChart
                      data={subplotKeys.map((key, i) => ({
                        x: indicators[key].map(p => p.date), y: indicators[key].map(p => p.value),
                        type: 'scatter', mode: 'lines', name: key.replace(/_/g, ' '),
                        line: { color: colors[i % colors.length], width: 1.5 },
                      }))}
                      layout={{
                        yaxis: { title: 'Indicator Value' },
                        showlegend: true, legend: { x: 0, y: 1, font: { size: 10 } },
                        shapes: [{
                          type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0,
                          line: { color: '#555', width: 1, dash: 'dot' },
                        }],
                      }}
                    />
                  </div>
                )}
              </>
            );
          })()}

          {/* Equity curve */}
          {equityCurveChart && (
            <div className={cardCls}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">EQUITY CURVE</h3>
              <PlotlyChart
                data={equityCurveChart}
                layout={{
                  yaxis: { title: 'Portfolio Value ($)', tickprefix: '$' },
                  showlegend: false,
                }}
              />
            </div>
          )}

          {/* Drawdown */}
          {drawdownChart && (
            <div className={cardCls}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">DRAWDOWN</h3>
              <PlotlyChart
                data={drawdownChart}
                layout={{
                  yaxis: { title: 'Drawdown (%)', ticksuffix: '%' },
                  showlegend: false,
                }}
              />
            </div>
          )}

          {/* Trade List */}
          {result.trades?.length > 0 && (
            <div className={cardCls}>
              <button
                onClick={() => setTradesOpen(!tradesOpen)}
                className="flex items-center justify-between w-full"
              >
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  TRADE LIST ({result.trades.length} trades)
                </h3>
                {tradesOpen ? <MdExpandLess className="text-gray-400" /> : <MdExpandMore className="text-gray-400" />}
              </button>

              {tradesOpen && (
                <div className="overflow-x-auto scrollbar-thin mt-3">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="px-3 py-2.5 text-left">#</th>
                        <th className="px-3 py-2.5 text-left">Entry</th>
                        <th className="px-3 py-2.5 text-left">Exit</th>
                        <th className="px-3 py-2.5 text-right">Entry $</th>
                        <th className="px-3 py-2.5 text-right">Exit $</th>
                        <th className="px-3 py-2.5 text-right">Size</th>
                        <th className="px-3 py-2.5 text-right">P&L</th>
                        <th className="px-3 py-2.5 text-right">Return</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {result.trades.map((t, i) => (
                        <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20">
                          <td className="px-3 py-2 text-gray-400 tabular-nums">{i + 1}</td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300 text-xs tabular-nums">{t.entry_date?.split('T')[0] || t.entry_date}</td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300 text-xs tabular-nums">{t.exit_date?.split('T')[0] || t.exit_date}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">${t.entry_price?.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">${t.exit_price?.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{t.size}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                            t.pnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                          }`}>
                            {t.pnl >= 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums text-xs ${
                            t.return_pct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                          }`}>
                            {t.return_pct >= 0 ? '+' : ''}{(t.return_pct * 100)?.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Saved indicator */}
          {strategyId && (
            <div className="text-center text-xs text-green-600 dark:text-green-400 mb-4">
              Results saved to strategy
            </div>
          )}
        </>
      )}

      {/* Results: Optimize mode */}
      {result && mode === 'optimize' && result.top_10 && (
        <>
          <div className={cardCls}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              OPTIMIZATION RESULTS &mdash; {result.symbol} ({result.combinations_tested} combinations tested)
            </h3>

            {/* Best result metrics */}
            {result.best && (
              <div className="mb-4 p-3 rounded-lg border border-brand-gold/30 bg-brand-gold/5">
                <div className="text-[10px] uppercase tracking-wider text-brand-gold font-semibold mb-2">BEST PARAMETERS</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(result.best.parameters).map(([k, v]) => (
                    <span key={k} className="text-xs px-2 py-1 rounded bg-brand-gold/10 text-brand-gold font-mono">
                      {k}: {v}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <MetricCard
                    label="RETURN"
                    value={`${result.best.total_return >= 0 ? '+' : ''}${result.best.total_return.toFixed(1)}%`}
                    valueColor={result.best.total_return >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                  />
                  <MetricCard
                    label="SHARPE"
                    value={result.best.sharpe_ratio.toFixed(2)}
                  />
                  <MetricCard
                    label="MAX DD"
                    value={`${result.best.max_drawdown.toFixed(1)}%`}
                    valueColor="text-red-500 dark:text-red-400"
                  />
                  <MetricCard
                    label="WIN RATE"
                    value={`${result.best.win_rate.toFixed(1)}%`}
                  />
                  <MetricCard
                    label="PROFIT F"
                    value={result.best.profit_factor.toFixed(2)}
                  />
                  <MetricCard
                    label="TRADES"
                    value={result.best.num_trades}
                  />
                </div>
              </div>
            )}

            {/* Top 10 table */}
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 mt-4">TOP 10 PARAMETER COMBINATIONS</h4>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 text-left">#</th>
                    <th className="px-3 py-2.5 text-left">Parameters</th>
                    <th className="px-3 py-2.5 text-right">Return</th>
                    <th className="px-3 py-2.5 text-right">Sharpe</th>
                    <th className="px-3 py-2.5 text-right">Max DD</th>
                    <th className="px-3 py-2.5 text-right">Win Rate</th>
                    <th className="px-3 py-2.5 text-right">Profit F</th>
                    <th className="px-3 py-2.5 text-right">Trades</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {result.top_10.map((r, i) => (
                    <tr key={i} className={i === 0 ? 'bg-brand-gold/5' : 'hover:bg-gray-50/50 dark:hover:bg-gray-800/20'}>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-700 dark:text-gray-300">
                        {Object.entries(r.parameters).map(([k, v]) => `${k}=${v}`).join(', ')}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                        r.total_return >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                      }`}>
                        {r.total_return >= 0 ? '+' : ''}{r.total_return.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.sharpe_ratio.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-500">{r.max_drawdown.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.win_rate.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.profit_factor.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.num_trades}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BacktestPage;
